<template>
    <div class="public-screens">
        <header class="public-screens__header">
            <div class="public-screens__brand">
                <img src="/images/2digital-horis.png" alt="2Digital" class="public-screens__logo" />
                <div class="public-screens__titles">
                    <h1>Карта экранов</h1>
                    <p>Выберите экраны и скачайте список в Excel</p>
                </div>
            </div>
            <a :href="authHref" class="public-screens__auth">{{ authLabel }}</a>
        </header>

        <div class="public-screens__toolbar">
            <p class="public-screens__hint">
                На карте {{ framesCount }} экранов.
                Фильтры города, улицы и типа — в списке слева.
                Отметьте нужные в списке или кликом по маркеру.
            </p>

            <p v-if="isOverLimit" class="public-screens__warn">
                Можно выгрузить не больше {{ maxExport }} экранов за раз.
            </p>

            <button
                type="button"
                class="btn btn--primary public-screens__export"
                :disabled="!canExport"
                @click="exportExcel"
            >
                <span v-if="!isExporting">Скачать Excel ({{ selectedFrameGids.length }})</span>
                <span v-else>Готовим файл…</span>
            </button>
        </div>

        <div class="public-screens__map">
            <GoogleMap
                :frames="frames"
                v-model:selectedFrameGids="selectedFrameGids"
                sidebar-mode="screens"
                :disable-price-check="true"
                :hide-required-star="true"
                :allow-multi-ssp="true"
                :fill-height="true"
                :allow-bulk-select="true"
                :show-week-requests="true"
                :show-request-details="false"
            />
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { ElNotification } from 'element-plus';
import GoogleMap from '../common/GoogleMap.vue';

const props = defineProps({
    frames: { type: Array, default: () => [] },
    isAuthenticated: { type: Boolean, default: false },
    homeUrl: { type: String, default: '/' },
    maxExport: { type: Number, default: 200 },
});

const selectedFrameGids = ref([]);
const isExporting = ref(false);

const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

const authHref = computed(() => (props.isAuthenticated ? props.homeUrl : '/login'));
const authLabel = computed(() => (props.isAuthenticated ? 'В кабинет' : 'Войти'));
const framesCount = computed(() => (Array.isArray(props.frames) ? props.frames.length : 0));

const isOverLimit = computed(() => selectedFrameGids.value.length > props.maxExport);
const canExport = computed(() =>
    selectedFrameGids.value.length > 0 && !isOverLimit.value && !isExporting.value,
);

const extractDownloadFilename = (response) => {
    const header = response.headers.get('Content-Disposition') || '';
    const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match) {
        return decodeURIComponent(utf8Match[1]);
    }

    const asciiMatch = header.match(/filename="?([^";]+)"?/i);
    return asciiMatch ? asciiMatch[1] : null;
};

const exportExcel = async () => {
    if (!canExport.value) {
        return;
    }

    isExporting.value = true;

    try {
        const response = await fetch('/screens/export', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRF-TOKEN': csrf,
            },
            body: JSON.stringify({ gids: selectedFrameGids.value }),
        });

        if (!response.ok) {
            let message = 'Не удалось скачать список экранов';
            const contentType = response.headers.get('Content-Type') || '';
            if (contentType.includes('application/json')) {
                const data = await response.json().catch(() => ({}));
                if (data.message) {
                    message = data.message;
                } else if (data.errors) {
                    message = Object.values(data.errors).flat()[0] || message;
                }
            } else if (response.status === 429) {
                message = 'Слишком много запросов. Подождите минуту и попробуйте снова.';
            }

            throw new Error(message);
        }

        const blob = await response.blob();
        const filename = extractDownloadFilename(response) || '2digital-screens.xlsx';
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
    } catch (error) {
        ElNotification({
            title: 'Ошибка',
            message: error instanceof Error ? error.message : 'Не удалось скачать список экранов',
            type: 'error',
        });
    } finally {
        isExporting.value = false;
    }
};
</script>
